import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();
const THEME_COLORS = {
  light: {
    background: '#f4efe7',
    backgroundAccent: 'radial-gradient(circle at top, rgba(255,255,255,0.95) 0%, rgba(244,239,231,1) 45%, rgba(231,223,210,1) 100%)',
    surface: '#fffaf2',
    surfaceElevated: '#ffffff',
    text: '#24303c',
    textMuted: '#6d7785',
    border: '#d8cfc1',
    button: '#e9dfd0',
    buttonText: '#24303c',
    accent: '#d97b39',
    accentText: '#fff9f4',
    positiveScore: '#2f9e66',
    negativeScore: '#d9485f',
    overlay: 'rgba(36, 48, 60, 0.6)',
    shadow: '0 18px 48px rgba(92, 74, 52, 0.14)',
    positiveGlow: 'rgba(217, 123, 57, 0.2)',
    negativeGlow: 'rgba(109, 119, 133, 0.18)',
    track: 'rgba(36, 48, 60, 0.08)',
    accentSoft: 'rgba(217, 123, 57, 0.14)',
    accentStrong: 'rgba(217, 123, 57, 0.82)',
  },
  dark: {
    background: '#141a22',
    backgroundAccent: 'radial-gradient(circle at top, rgba(38,52,69,0.96) 0%, rgba(20,26,34,1) 52%, rgba(12,16,21,1) 100%)',
    surface: '#1b2430',
    surfaceElevated: '#243141',
    text: '#edf2f7',
    textMuted: '#a7b1bf',
    border: '#334254',
    button: '#243141',
    buttonText: '#edf2f7',
    accent: '#ff8a3d',
    accentText: '#1a202c',
    positiveScore: '#58d68d',
    negativeScore: '#ff6b81',
    overlay: 'rgba(7, 10, 14, 0.78)',
    shadow: '0 18px 48px rgba(0, 0, 0, 0.34)',
    positiveGlow: 'rgba(255, 138, 61, 0.22)',
    negativeGlow: 'rgba(167, 177, 191, 0.16)',
    track: 'rgba(237, 242, 247, 0.1)',
    accentSoft: 'rgba(255, 138, 61, 0.16)',
    accentStrong: 'rgba(255, 138, 61, 0.85)',
  },
};

export function ThemeProvider({ children }) {
  const [isDark, setIsDark] = useState(true); // Default to dark theme

  const theme = {
    isDark,
    toggleTheme: () => {
      setIsDark(!isDark);
      localStorage.setItem('darkMode', !isDark);
    },
    colors: isDark ? THEME_COLORS.dark : THEME_COLORS.light
  };

  // Load theme preference from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('darkMode');
    if (savedTheme !== null) {
      setIsDark(savedTheme === 'true');
    }
  }, []);

  useEffect(() => {
    const activeColors = isDark ? THEME_COLORS.dark : THEME_COLORS.light;
    const root = document.documentElement;

    root.style.setProperty('--app-bg', activeColors.background);
    root.style.setProperty('--app-bg-accent', activeColors.backgroundAccent);
    root.style.setProperty('--app-surface', activeColors.surface);
    root.style.setProperty('--app-surface-elevated', activeColors.surfaceElevated);
    root.style.setProperty('--app-text', activeColors.text);
    root.style.setProperty('--app-text-muted', activeColors.textMuted);
    root.style.setProperty('--app-border', activeColors.border);
    root.style.setProperty('--app-button', activeColors.button);
    root.style.setProperty('--app-button-text', activeColors.buttonText);
    root.style.setProperty('--app-accent', activeColors.accent);
    root.style.setProperty('--app-accent-text', activeColors.accentText);
    root.style.setProperty('--app-positive', activeColors.positiveScore);
    root.style.setProperty('--app-negative', activeColors.negativeScore);
    root.style.setProperty('--app-shadow', activeColors.shadow);
    root.style.setProperty('--app-positive-glow', activeColors.positiveGlow);
    root.style.setProperty('--app-negative-glow', activeColors.negativeGlow);
    root.style.setProperty('--app-track', activeColors.track);
    root.style.setProperty('--app-accent-soft', activeColors.accentSoft);
    root.style.setProperty('--app-accent-strong', activeColors.accentStrong);
    root.style.setProperty('color-scheme', isDark ? 'dark' : 'light');
  }, [isDark]);

  return (
    <ThemeContext.Provider value={theme}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
